import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {useIntersectionObserver} from '../../hooks/useIntersectionObserver';

/**
 * Styled Components
 */
const SectionContainer = styled.div`
    width: 100%;
    min-height: ${props => props.minHeight || 'auto'};
`;

const SkeletonWrapper = styled.div`
    width: 100%;
    min-height: ${props => props.minHeight || '500px'};
    background-color: transparent;
`;

/**
 * LazySection Component
 * Wrapper component that lazy loads children when they become visible in viewport
 * Displays skeleton while loading for better UX
 *
 * @param {React.ReactNode} children - Content to lazy load
 * @param {React.ReactNode} skeleton - Custom skeleton component (optional)
 * @param {Function} onVisible - Callback when section becomes visible
 * @param {string} minHeight - Minimum height for container (prevents layout shift)
 * @param {number} threshold - Intersection observer threshold (0-1)
 * @param {string} rootMargin - Margin around root element (e.g., '200px')
 */
const LazySection = ({
	                     children,
	                     skeleton,
	                     onVisible,
	                     minHeight = '300px',
	                     threshold = 0,
	                     rootMargin = '200px',
                     }) => {
	const [hasLoaded, setHasLoaded] = useState(false);

	// Use intersection observer to detect when section becomes visible
	const {ref, isVisible} = useIntersectionObserver({
		threshold,
		rootMargin,
		triggerOnce: true,
	});

	// Trigger callback when section becomes visible
	useEffect(() => {
		if (isVisible && !hasLoaded) {
			setHasLoaded(true);
			if (onVisible) {
				onVisible();
			}
		}
	}, [isVisible, hasLoaded, onVisible]);

	return (
		<SectionContainer ref={ref} minHeight={minHeight}>
			{hasLoaded ? children : (skeleton || <SkeletonWrapper minHeight={minHeight}/>)}
		</SectionContainer>
	);
};

LazySection.propTypes = {
	children: PropTypes.node.isRequired,
	skeleton: PropTypes.node,
	onVisible: PropTypes.func,
	minHeight: PropTypes.string,
	threshold: PropTypes.number,
	rootMargin: PropTypes.string,
};

export default React.memo(LazySection);
